import { getApiBase } from "../utils/env";
const BASE = getApiBase();

/** GET /api/update/status — current vs latest version (cached 4 h server-side) */
export async function getUpdateStatus(token) {
  const res = await fetch(`${BASE}/api/update/status`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Update status fetch failed: ${res.status}`);
  return res.json();
}

/** POST /api/update/check — force-refresh from npm registry */
export async function forceCheckUpdate(token) {
  const res = await fetch(`${BASE}/api/update/check`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Update check failed: ${res.status}`);
  return res.json();
}

/**
 * POST /api/update/apply[?version=X.Y.Z]
 * Returns { jobId, targetVersion, message }
 */
export async function applyUpdate(token, version) {
  const qs = version ? `?version=${encodeURIComponent(version)}` : "";
  const res = await fetch(`${BASE}/api/update/apply${qs}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Apply update failed: ${res.status}`);
  }
  return res.json();
}

/** GET /api/update/progress/:jobId — polling snapshot */
export async function getUpdateProgress(token, jobId) {
  const res = await fetch(`${BASE}/api/update/progress/${jobId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Progress fetch failed: ${res.status}`);
  return res.json();
}

/** GET /api/admin/site-url — current site URL from index.html */
export async function getSiteUrl(token) {
  const res = await fetch(`${BASE}/api/admin/site-url`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  return res.json();
}

/**
 * POST /api/admin/reconfigure-url — update site URL in index.html + appsettings.
 * `downloadBaseUrl` is optional: omit it (undefined/null) and the server leaves
 * ApplicationSettings:PublicDownloadBaseUrl untouched; pass "" to clear it so download
 * links fall back to the origin of the request that asked for them.
 */
export async function reconfigureSiteUrl(token, siteUrl, downloadBaseUrl) {
  const payload = { siteUrl };
  if (downloadBaseUrl !== undefined && downloadBaseUrl !== null)
    payload.downloadBaseUrl = downloadBaseUrl;
  const res = await fetch(`${BASE}/api/admin/reconfigure-url`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Reconfigure failed: ${res.status}`);
  return body;
}

/** POST /api/update/dismiss/:jobId — mark a stuck job as failed so the UI can reset */
export async function dismissUpdateJob(token, jobId) {
  const res = await fetch(`${BASE}/api/update/dismiss/${jobId}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Dismiss failed: ${res.status}`);
  }
  return res.json();
}

/** GET /api/update/components — FileVersionInfo for each installed component DLL */
export async function getComponentVersions(token) {
  const res = await fetch(`${BASE}/api/update/components`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Component versions fetch failed: ${res.status}`);
  return res.json();
}

/** GET /api/update/last — most recent job (after restart) */
export async function getLastUpdateJob(token) {
  const res = await fetch(`${BASE}/api/update/last`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  const body = await res.json();
  return body || null;
}

/**
 * Opens an SSE connection to /api/update/stream/:jobId.
 * onLine(text)    — called for each log line
 * onStatus(phase) — called when the job phase changes (e.g. "installing_package")
 * onDone(ok)      — called when the update finishes (ok=true) or fails (ok=false)
 * onError()       — called if the connection drops (service restarting)
 * Returns a close() function.
 */
export function openUpdateStream(token, jobId, { onLine, onStatus, onDone, onError }) {
  const ctrl = new AbortController();
  let receivedDone = false;

  (async () => {
    try {
      const res = await fetch(`${BASE}/api/update/stream/${jobId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "text/event-stream",
        },
        signal: ctrl.signal,
      });

      if (!res.ok || !res.body) {
        onError?.();
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() ?? "";
        for (const part of parts) {
          const dataLine = part.split("\n").find((l) => l.startsWith("data:"));
          if (!dataLine) continue;
          try {
            const payload = JSON.parse(dataLine.slice(5).trim());
            if (payload.done !== undefined) {
              receivedDone = true;
              onDone?.(payload.status === "complete");
            } else if (payload.text) {
              onLine?.(payload.text);
            } else if (payload.status) {
              // Phase change emitted by the updater (not a terminal event)
              onStatus?.(payload.status);
            }
          } catch {
            /* skip malformed events */
          }
        }
      }

      // Stream ended without a done event — the updater's idle timeout fired
      // or the API restarted. Switch to polling so the client stays up-to-date.
      if (!receivedDone) {
        onError?.();
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        onError?.();
      }
    }
  })();

  return () => ctrl.abort();
}

// ── Fleet ────────────────────────────────────────────────────────────────────
// The other servers of this installation: HA peers and ingest servers, which have
// no update button of their own. Read and driven from this one.

/** GET /api/Admin/fleet/versions — what every registered server reports it is running */
export async function getFleetVersions(token, { refresh = false } = {}) {
  const res = await fetch(`${BASE}/api/Admin/fleet/versions?refresh=${refresh ? "true" : "false"}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/** POST /api/Admin/fleet/update — update the servers that are behind this one */
export async function updateFleet(token, { hostIds = null, force = false } = {}) {
  const res = await fetch(`${BASE}/api/Admin/fleet/update`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ hostIds, force }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || (await res.text()));
  return body;
}

/** GET /api/Admin/fleet/status — one line per server, for polling while a run is in flight */
export async function getFleetStatus(token) {
  const res = await fetch(`${BASE}/api/Admin/fleet/status`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ── Topology ─────────────────────────────────────────────────────────────────
// The installation as a set of MACHINES rather than a set of services. Joins the
// servers that registered themselves (a node agent runs on every one) with the
// hosts an operator entered, and reports where the two do not line up.

/** GET /api/Admin/fleet/topology — every machine, what runs on it, and whether it has been heard from */
export async function getFleetTopology(token) {
  const res = await fetch(`${BASE}/api/Admin/fleet/topology`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/**
 * POST /api/Admin/fleet/topology/link — confirm (or withdraw) that a self-registered
 * server and a host row are the same machine. Pass hostConfigurationId: null to unlink.
 *
 * This is deliberately an operator's decision and never inferred: once linked, actions
 * aimed at that machine use the host's address and key.
 */
export async function linkTopologyNode(token, { nodeId, hostConfigurationId }) {
  const res = await fetch(`${BASE}/api/Admin/fleet/topology/link`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ nodeId, hostConfigurationId: hostConfigurationId ?? null }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || (await res.text()));
  return body;
}

// ── Commands ─────────────────────────────────────────────────────────────────
// Acting on a machine. With an agent the command is queued and the machine picks it
// up; without one it runs over SSH inline. Both are the same call and the same log.

/** POST /api/Admin/fleet/command — start/stop/restart a service on one machine. */
export async function queueFleetCommand(token, { nodeId, hostId, command, argument, correlationId }) {
  const res = await fetch(`${BASE}/api/Admin/fleet/command`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ nodeId, hostId, command, argument, correlationId }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || (await res.text()));
  return body;
}

/** GET /api/Admin/fleet/commands — recent commands, newest first. */
export async function getFleetCommands(token, { nodeId = null, limit = 50 } = {}) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (nodeId) qs.set("nodeId", String(nodeId));
  const res = await fetch(`${BASE}/api/Admin/fleet/commands?${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/** POST /api/Admin/fleet/commands/{id}/cancel — withdraw one that has not been picked up. */
export async function cancelFleetCommand(token, id) {
  const res = await fetch(`${BASE}/api/Admin/fleet/commands/${id}/cancel`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || (await res.text()));
  return body;
}

/**
 * Opens the fleet change stream. OPTIONAL and purely an accelerator: it carries no data, only a token
 * saying something moved, and the page re-reads the topology the same way it always does. The caller
 * keeps its own polling running regardless, so a stream that is switched off, blocked by a proxy, or
 * simply broken changes nothing except how soon the screen catches up.
 *
 * Uses fetch + a reader rather than EventSource because EventSource cannot send an Authorization
 * header — the same reason openUpdateStream does.
 *
 * onChange() — called when the fleet has moved. Returns a close() function.
 */
export function openFleetStream(token, { onChange, onError }) {
  const ctrl = new AbortController();

  (async () => {
    try {
      const res = await fetch(`${BASE}/api/Admin/fleet/stream`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "text/event-stream" },
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) { onError?.(); return; }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() ?? "";
        for (const part of parts) {
          const line = part.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;                       // keep-alive comment frame
          try {
            if (JSON.parse(line.slice(5).trim())?.changed) onChange?.();
          } catch { /* skip malformed events */ }
        }
      }
      // The server closes the stream on its own after a while so a connection cannot be held open
      // through a proxy that has stopped delivering. Tell the caller so it can reopen.
      onError?.();
    } catch (err) {
      if (err.name !== "AbortError") onError?.();
    }
  })();

  return () => ctrl.abort();
}
